import type { FlagGuardrailArm } from 'definitions';
import type { Flag } from '@/state/flags/flag-types';
import type { GuardrailCheck } from '@/state/flags/flag-guardrail';
import { cn } from 'helpers/cn';
import { conversionRate, formatCount, relativeInterval } from '@/state/flags/flag-guardrail';
import { percentOf } from '@/state/flags/flag-status';
import { useFlagGuardrail } from '@/state/flags/useFlagGuardrail';
import { GuardrailVerdictChip } from '../FlagChips';
import { FlagSection } from './FlagSection';
import { FLAG_COPY } from '../copy';

type FlagGuardrailProps = { flag: Flag };

const COPY = FLAG_COPY.guardrail;

/**
 * The guardrail's last check of the step the plan is on (PER-340): its verdict and what it
 * means for the plan, the visitors and conversions each side had during the step, and the
 * always-valid interval it judged. A check of an earlier step, or of this one before a resume
 * restarted it, is not shown: the scheduler would not act on it either.
 */
export const FlagGuardrail = ({ flag }: FlagGuardrailProps) => {
  const guardrail = useFlagGuardrail(flag);
  const { check } = guardrail;

  return (
    <FlagSection
      title={COPY.title}
      help={COPY.help}
      description={COPY.description}
      tourAnchor="flag-guardrail"
      actions={<GuardrailVerdictChip verdict={guardrail.verdict} />}
    >
      <div className="flex flex-col gap-3">
        <p className={cn('m-0 text-body', guardrail.verdict === 'breach' ? 'text-danger' : 'text-foreground')}>
          {check ? COPY.explain[check.verdict] : guardrail.isDraft ? COPY.noCheckDraft : COPY.noCheck}
        </p>

        {check && <CheckDetails check={check} marginBp={guardrail.marginBp} checkedAgo={guardrail.checkedAgo ?? ''} />}

        {guardrail.visitorIdOff && <p className="supporting text-warn m-0">{COPY.noVisitorId}</p>}
        <p className="supporting m-0">{COPY.limit}</p>
      </div>
    </FlagSection>
  );
};

type CheckDetailsProps = { check: GuardrailCheck; marginBp: number; checkedAgo: string };

const CheckDetails = ({ check, marginBp, checkedAgo }: CheckDetailsProps) => (
  <div className="flex flex-col gap-2">
    <div className="border-line rounded-card overflow-x-auto border">
      <table className="w-full min-w-[320px] border-collapse text-meta">
        <thead>
          <tr className="border-line border-b">
            <th className="micro-label px-3 py-2 text-left font-normal">{COPY.side}</th>
            <th className="micro-label px-3 py-2 text-right font-normal">{COPY.visitors}</th>
            <th className="micro-label px-3 py-2 text-right font-normal">{COPY.conversions}</th>
            <th className="micro-label px-3 py-2 text-right font-normal">{COPY.rate}</th>
          </tr>
        </thead>
        <tbody>
          <ArmRow label={COPY.on} arm={check.on} />
          <ArmRow label={COPY.off} arm={check.off} />
        </tbody>
      </table>
    </div>
    <p className="supporting m-0">{COPY.stepVisitors}</p>

    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
      <span className="text-foreground num text-body">
        {check.relative ? COPY.interval(relativeInterval(check.relative)) : COPY.intervalPending}
      </span>
      <span className="supporting">{COPY.margin(percentOf(marginBp))}</span>
    </div>

    <p className="supporting m-0">
      {COPY.goal} <span className="text-foreground font-mono">{check.event}</span>
      <span className="mx-2">·</span>
      {COPY.checked(checkedAgo)}
    </p>
  </div>
);

type ArmRowProps = { label: string; arm: FlagGuardrailArm };

const ArmRow = ({ label, arm }: ArmRowProps) => (
  <tr className="border-line border-b last:border-b-0">
    <td className="text-foreground px-3 py-2">{label}</td>
    <td className="num px-3 py-2 text-right">{formatCount(arm.visitors)}</td>
    <td className="num px-3 py-2 text-right">{formatCount(arm.conversions)}</td>
    <td className="num px-3 py-2 text-right">{conversionRate(arm) ?? '0%'}</td>
  </tr>
);
